import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  Select,
  Table,
  Tag,
  TagLabel,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  VStack,
  Wrap,
  WrapItem,
  Spinner,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  AddIcon,
  DeleteIcon,
  EditIcon,
  DownloadIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  TriangleUpIcon,
  TriangleDownIcon,
  CheckIcon,
} from "@chakra-ui/icons";
import { FiUpload } from "react-icons/fi";
import { FaBuilding } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  listClients,
  listContacts,
  updateClientContact,
  listClientContacts,
  exportClients,
  getPendingDeleteSummary,
} from "../../../api/clientsApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { PLATFORM_ROLES, BU_ROLES } from "../../../constants/roles";
import {
  CLIENT_STATUSES,
  CLIENT_STATUS_LABELS,
  CLIENT_STATUS_LABEL_KEYS,
  CLIENT_STATUS_COLORS,
  CONTACT_STATUSES,
  CONTACT_STATUS_LABELS,
  CONTACT_STATUS_LABEL_KEYS,
  CONTACT_STATUS_COLORS,
  getStatusLabel,
} from "../../../constants/statuses";
import ClientFormModal from "./ClientFormModal";
import ContactFormModal from "./ContactFormModal";
import ImportWorkbookModal from "./ImportWorkbookModal";
import ClientContactsWizardModal from "./ClientContactsWizardModal";
import ClientDetailModal from "./ClientDetailModal";
import DeleteCompanyConfirmModal from "./DeleteCompanyConfirmModal";
import BulkApproveDeletionsModal from "./BulkApproveDeletionsModal";
import {
  familyColor,
  MAX_TOP_SKILLS_KANBAN,
  normalizeTopSkills,
} from "../../../utils/pilotSkills";
import {
  PageHeader,
  Panel,
  DataTable,
  StatusBadge,
  MiniIconButton,
  FilterBar,
  FilterSearch,
  EmptyState,
  TablePagination,
} from "../../../components/Portal/ui";

// Compact filter Select matching the template's 31px controls.
const filterSelectSx = {
  height: "31px !important",
  paddingTop: "0 !important",
  paddingBottom: "0 !important",
  lineHeight: "31px !important",
  fontSize: "12px",
  fontWeight: 600,
  bg: "white",
};

// Date/range inputs in the secondary filter row.
const rangeInputSx = {
  height: "31px !important",
  fontWeight: 600,
  fontSize: "12px",
  bg: "white",
};

// Tiny uppercase label for filter groups (template `.eyebrow` scale).
const filterLabelSx = {
  textTransform: "uppercase",
  fontSize: "10px",
  fontWeight: 800,
  letterSpacing: "0.055em",
  color: "portal.muted",
};

// Clickable sort header node, rendered inside DataTable's column label so the
// tiny uppercase header stays consistent with the template while remaining
// sortable. Preserves the original aria-sort + a stable data-testid contract.
const SortLabel = ({ label, columnKey, sortConfig, onSort, align }) => {
  const active = sortConfig.key === columnKey;
  const ariaSort = active
    ? sortConfig.direction === "asc"
      ? "ascending"
      : "descending"
    : "none";
  return (
    <Flex
      as="span"
      display="inline-flex"
      align="center"
      gap="4px"
      cursor="pointer"
      userSelect="none"
      onClick={() => onSort(columnKey)}
      aria-sort={ariaSort}
      data-testid={`sort-header-${columnKey}`}
      justify={align === "right" ? "flex-end" : "flex-start"}
      w={align === "right" ? "100%" : undefined}
      _hover={{ color: "portal.primary" }}
    >
      <Box as="span">{label}</Box>
      {active && sortConfig.direction === "asc" && <TriangleUpIcon boxSize="9px" />}
      {active && sortConfig.direction === "desc" && <TriangleDownIcon boxSize="9px" />}
    </Flex>
  );
};

const Clients = () => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { effectiveRole, selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();

  // A BU Manager is admin-equivalent (same as BU Admin / Company Admin) and
  // may delete companies. Non-admins can edit but not delete.
  const canDelete = useMemo(() => {
    return effectiveRole === PLATFORM_ROLES.COMPANY_ADMIN ||
           effectiveRole === BU_ROLES.ADMIN ||
           effectiveRole === BU_ROLES.MANAGER;
  }, [effectiveRole]);

  const canConfirmDelete = useMemo(() => {
    return effectiveRole === PLATFORM_ROLES.COMPANY_ADMIN ||
           effectiveRole === BU_ROLES.ADMIN ||
           effectiveRole === BU_ROLES.MANAGER;
  }, [effectiveRole]);

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPagesFromApi, setTotalPagesFromApi] = useState(null);
  const [viewMode, setViewMode] = useState("companies");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Filters
  const [search, setSearch] = useState("");
  const [filterSector, setFilterSector] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterManagerId, setFilterManagerId] = useState("");
  const [contactCreatedFrom, setContactCreatedFrom] = useState("");
  const [contactCreatedTo, setContactCreatedTo] = useState("");
  const [buUsers, setBuUsers] = useState([]);

  // Modals
  const createModal = useDisclosure();
  const editModal = useDisclosure();
  const importWorkbookModal = useDisclosure();
  const createContactWithoutCompanyModal = useDisclosure();
  const contactWizardModal = useDisclosure();
  const [editingItem, setEditingItem] = useState(null);
  const [createdClientForContacts, setCreatedClientForContacts] = useState(null);
  const [expandedClientId, setExpandedClientId] = useState(null);
  const [clientContacts, setClientContacts] = useState({});
  const clientDetailModal = useDisclosure();
  const deleteModal = useDisclosure();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const bulkDeleteModal = useDisclosure();
  // Count of companies awaiting Excel-initiated delete approval across the
  // whole BU (not just the current page) — gates the "approve all" button.
  const [pendingDeleteCount, setPendingDeleteCount] = useState(0);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [detailInitialTab, setDetailInitialTab] = useState(0);

  const isClientsView = viewMode === "clients";
  const isStatusView = viewMode === "status";
  const isContactStatusView = viewMode === "contactStatus";
  const isKanbanView = isStatusView || isContactStatusView;

  const fetchData = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      setLoading(true);
    }
    try {
      const listFn = (isClientsView || isContactStatusView) ? listContacts : listClients;
      const isColumnView = isStatusView || isContactStatusView;
      const res = await listFn(
        {
          search: search || undefined,
          sector: filterSector || undefined,
          status: filterStatus || undefined,
          managerId: filterManagerId || undefined,
          contactCreatedFrom: contactCreatedFrom || undefined,
          contactCreatedTo: contactCreatedTo || undefined,
          ...(isColumnView ? { unpaginated: true } : { page, pageSize })
        },
        { buId: selectedBusinessUnitId || undefined }
      );
      setItems(res.items || []);
      setTotalItems(res.totalItems || 0);
      setTotalPagesFromApi(typeof res.totalPages === "number" ? res.totalPages : null);
    } catch (e) {
      toast({ title: t("pilotClients.list.toasts.loadClientsFailed"), status: "error" });
      setItems([]);
      setTotalItems(0);
      setTotalPagesFromApi(null);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [
    search,
    filterSector,
    filterStatus,
    filterManagerId,
    contactCreatedFrom,
    contactCreatedTo,
    page,
    pageSize,
    isClientsView,
    isStatusView,
    isContactStatusView,
    selectedBusinessUnitId,
    toast,
    t
  ]);

  // Refresh the BU-wide pending-delete count that gates the "approve all"
  // button. Only admins (canConfirmDelete) may approve, so others skip the call.
  const loadPendingDeleteCount = useCallback(async () => {
    if (!canConfirmDelete) {
      setPendingDeleteCount(0);
      return;
    }
    try {
      const res = await getPendingDeleteSummary({ buId: selectedBusinessUnitId || undefined });
      setPendingDeleteCount(res?.clientCount || 0);
    } catch (e) {
      setPendingDeleteCount(0);
    }
  }, [canConfirmDelete, selectedBusinessUnitId]);

  const normalizeClientStatus = (value) => {
    if (!value) return "";
    return String(value).toLowerCase();
  };

  // Client status is auto-derived — drag-and-drop is read-only for companies
  const handleStatusDrop = () => {
    toast({
      title: t("pilotClients.list.toasts.statusReadOnly", { defaultValue: "Company status is automatically calculated and cannot be changed manually." }),
      status: "info"
    });
  };

  const handleContactStatusDrop = async (contactId, nextStatus) => {
    const current = items.find((item) => item.id === contactId);
    if (!current || current.status === nextStatus) return;
    const previousStatus = current.status;
    const clientId = current.client?.id || current.client_id;
    if (!clientId) return;
    setItems((prev) => prev.map((item) => (
      item.id === contactId ? { ...item, status: nextStatus } : item
    )));
    try {
      await updateClientContact(clientId, contactId, { status: nextStatus }, { buId: selectedBusinessUnitId || undefined });
      toast({ title: t("pilotClients.list.toasts.updateContactStatusSuccess"), status: "success" });
    } catch (e) {
      setItems((prev) => prev.map((item) => (
        item.id === contactId ? { ...item, status: previousStatus } : item
      )));
      toast({
        title: t("pilotClients.list.toasts.updateContactStatusFailedTitle"),
        description: e?.response?.data?.message || t("pilotClients.list.toasts.updateContactStatusFailedDescription"),
        status: "error"
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    loadPendingDeleteCount();
  }, [loadPendingDeleteCount]);

  // Refetch when Business Unit context changes (after BU context is ready)
  useEffect(() => {
    if (!isBUContextLoaded) return;
    setPage(1);
    fetchData();
    loadPendingDeleteCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBUContextLoaded, selectedBusinessUnitId]);

  // Auto-refresh clients every 30 seconds (silent refresh)
  useEffect(() => {
    if (!isBUContextLoaded) return;
    const intervalId = setInterval(() => {
      fetchData({ silent: true });
      loadPendingDeleteCount();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [fetchData, loadPendingDeleteCount, isBUContextLoaded]);

  useEffect(() => {
    // Fetch BU users for manager filter dropdown
    import("../../../api/clientsApi").then(({ listUsersInMyBusinessUnit }) => {
      listUsersInMyBusinessUnit({ buId: selectedBusinessUnitId || undefined }).then(setBuUsers).catch(() => setBuUsers([]));
    });
  }, [selectedBusinessUnitId]);

  const onCreateSuccess = (created) => {
    if (created?._viewExisting) {
      // Duplicate was found — open the existing client detail
      handleView(created.id);
      return;
    }
    setPage(1);
    fetchData();
    if (created && created.id) {
      setCreatedClientForContacts(created);
      contactWizardModal.onOpen();
    }
  };

  const onEditSuccess = () => {
    fetchData();
  };

  // Open the typed-confirmation modal. Used both for a direct delete and for
  // confirming an Excel-initiated pending delete; the modal shows the full
  // impact summary and performs the irreversible cascade on confirm.
  const openDeleteModal = (item) => {
    setDeleteTarget({ id: item.id, name: item.name });
    deleteModal.onOpen();
  };

  const handleView = (id) => {
    setSelectedClientId(id);
    setDetailInitialTab(0);
    clientDetailModal.onOpen();
  };

  const handleContactRowClick = (clientId) => {
    setSelectedClientId(clientId);
    setDetailInitialTab(1);
    clientDetailModal.onOpen();
  };

  const loadClientContacts = useCallback(async (clientId) => {
    if (!clientId) return;
    setClientContacts((prev) => ({
      ...prev,
      [clientId]: {
        ...(prev[clientId] || {}),
        loading: true
      }
    }));
    try {
      const res = await listClientContacts(clientId, { buId: selectedBusinessUnitId || undefined });
      const all = Array.isArray(res) ? res : [];
      // Hide inactive contacts in the main list view (but keep them visible on the Client detail page)
      const visible = all.filter((c) => {
        const v =
          c?.is_active != null
            ? c.is_active
            : c?.active != null
            ? c.active
            : true;
        return v !== false;
      });
      setClientContacts((prev) => ({
        ...prev,
        [clientId]: {
          loading: false,
          items: visible
        }
      }));
    } catch (e) {
      toast({ title: t("pilotClients.list.toasts.loadContactsFailed"), status: "error" });
      setClientContacts((prev) => ({
        ...prev,
        [clientId]: {
          loading: false,
          items: []
        }
      }));
    }
  }, [selectedBusinessUnitId, toast, t]);

  const canExport = useMemo(() => {
    return effectiveRole === PLATFORM_ROLES.COMPANY_ADMIN ||
           effectiveRole === BU_ROLES.ADMIN ||
           effectiveRole === BU_ROLES.MANAGER ||
           effectiveRole === BU_ROLES.SALES_MANAGER ||
           effectiveRole === BU_ROLES.RECRUITMENT_MANAGER ||
           effectiveRole === BU_ROLES.HR_MANAGER;
  }, [effectiveRole]);

  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportClients({}, { buId: selectedBusinessUnitId || undefined });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `clients_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: t("common.error", { defaultValue: "Error" }),
        description: t("pilotClients.list.exportError", { defaultValue: "Failed to export clients." }),
        status: "error",
        duration: 5000
      });
    } finally {
      setExporting(false);
    }
  };

  const handleToggleExpand = (clientId) => {
    const willExpand = expandedClientId !== clientId;
    setExpandedClientId(willExpand ? clientId : null);
    if (willExpand && !clientContacts[clientId]) {
      loadClientContacts(clientId);
    }
  };

  const totalPages = useMemo(() => {
    return totalPagesFromApi || Math.max(1, Math.ceil(totalItems / pageSize));
  }, [totalPagesFromApi, totalItems, pageSize]);

  const collator = useMemo(() => new Intl.Collator(undefined, { numeric: true, sensitivity: "base" }), []);

  const compareValues = useCallback((a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === "number" && typeof b === "number") return a - b;
    return collator.compare(String(a), String(b));
  }, [collator]);

  const getSortValue = useCallback((item, key) => {
    if (isClientsView) {
      const client = item.client || {};
      switch (key) {
        case "contactName":
          return item.name;
        case "contactFunction":
          return item.title;
        case "contactDepartment":
          return item.division;
        case "contactStatus":
          return item.status
            ? getStatusLabel(t, CONTACT_STATUS_LABEL_KEYS, CONTACT_STATUS_LABELS, item.status)
            : "";
        case "contactEmail":
          return item.email;
        case "contactPhone":
          return item.phone;
        case "companyName":
          return client.name;
        case "companySector":
          return client.sector;
        case "companyLocation":
          return client.location;
        case "companyManager":
          return client.managerName;
        default:
          return "";
      }
    }
    switch (key) {
      case "name":
        return item.name;
      case "sector":
        return item.sector;
      case "status":
        return getStatusLabel(
          t,
          CLIENT_STATUS_LABEL_KEYS,
          CLIENT_STATUS_LABELS,
          normalizeClientStatus(item.status ?? item.client_status ?? item.clientStatus)
        );
      case "location":
        return item.location;
      case "manager":
        return item.managerName;
      default:
        return "";
    }
  }, [isClientsView, normalizeClientStatus, t]);

  const sortedItems = useMemo(() => {
    if (isStatusView || isContactStatusView || !sortConfig.key) return items;
    const nextItems = [...items];
    nextItems.sort((a, b) => compareValues(getSortValue(a, sortConfig.key), getSortValue(b, sortConfig.key)));
    if (sortConfig.direction === "desc") nextItems.reverse();
    return nextItems;
  }, [items, sortConfig, isStatusView, isContactStatusView, compareValues, getSortValue]);

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      return { key: null, direction: "asc" };
    });
  }, []);

  useEffect(() => {
    if (isStatusView || isContactStatusView) {
      setSortConfig({ key: null, direction: "asc" });
      return;
    }
    const allowedKeys = isClientsView
      ? new Set([
        "contactName",
        "contactFunction",
        "contactDepartment",
        "contactStatus",
        "contactEmail",
        "contactPhone",
        "companyName",
        "companySector",
        "companyLocation",
        "companyManager"
      ])
      : new Set(["name", "sector", "status", "location", "manager"]);
    if (sortConfig.key && !allowedKeys.has(sortConfig.key)) {
      setSortConfig({ key: null, direction: "asc" });
    }
  }, [isClientsView, isStatusView, isContactStatusView, sortConfig.key]);

  const sortHeader = (label, columnKey, align) => (
    <SortLabel
      label={label}
      columnKey={columnKey}
      sortConfig={sortConfig}
      onSort={handleSort}
      align={align}
    />
  );

  // Status filter options depend on the active view.
  const statusFilterOptions = (isClientsView || isContactStatusView)
    ? CONTACT_STATUSES.map((s) => ({
        value: s.value,
        label: getStatusLabel(t, CONTACT_STATUS_LABEL_KEYS, CONTACT_STATUS_LABELS, s.value),
      }))
    : CLIENT_STATUSES.map((s) => ({
        value: s.value,
        label: getStatusLabel(t, CLIENT_STATUS_LABEL_KEYS, CLIENT_STATUS_LABELS, s.value),
      }));

  const companyColumns = [
    { key: "contacts", label: t("pilotClients.list.table.contacts"), width: "8%" },
    { key: "name", label: sortHeader(t("pilotClients.list.table.name"), "name"), width: "24%" },
    { key: "sector", label: sortHeader(t("pilotClients.list.table.sector"), "sector"), width: "16%" },
    { key: "status", label: sortHeader(t("pilotClients.list.table.status"), "status"), width: "16%" },
    { key: "location", label: sortHeader(t("pilotClients.list.table.location"), "location"), width: "14%" },
    { key: "manager", label: sortHeader(t("pilotClients.list.table.manager"), "manager"), width: "14%" },
    { key: "actions", label: t("pilotClients.list.table.actions"), width: "12%", align: "right" },
  ];

  const contactColumns = [
    { key: "contactName", label: sortHeader(t("pilotClients.list.table.contactName"), "contactName"), width: "13%" },
    { key: "contactFunction", label: sortHeader(t("pilotClients.list.table.contactFunction"), "contactFunction"), width: "11%" },
    { key: "contactDepartment", label: sortHeader(t("pilotClients.list.table.contactDepartment"), "contactDepartment"), width: "11%" },
    { key: "contactStatus", label: sortHeader(t("pilotClients.list.table.contactStatus"), "contactStatus"), width: "12%" },
    { key: "contactEmail", label: sortHeader(t("pilotClients.list.table.contactEmail"), "contactEmail"), width: "14%" },
    { key: "contactPhone", label: sortHeader(t("pilotClients.list.table.contactPhone"), "contactPhone"), width: "11%" },
    { key: "companyName", label: sortHeader(t("pilotClients.list.table.companyName"), "companyName"), width: "11%" },
    { key: "companySector", label: sortHeader(t("pilotClients.list.table.companySector"), "companySector"), width: "9%" },
    { key: "companyLocation", label: sortHeader(t("pilotClients.list.table.companyLocation"), "companyLocation"), width: "8%" },
    { key: "companyManager", label: sortHeader(t("pilotClients.list.table.companyManager"), "companyManager"), width: "10%" },
  ];

  const pageTitle = isStatusView
    ? t("pilotClients.list.pageTitleStatus")
    : isContactStatusView
    ? t("pilotClients.list.pageTitleContactStatus")
    : isClientsView
    ? t("pilotClients.list.pageTitleContacts")
    : t("pilotClients.list.pageTitle");

  // Renders one company row (+ its expandable contacts sub-table).
  const renderCompanyRow = (item) => {
    const pendingDeleteAt = item.pendingDeleteAt || item.pending_delete_at;
    const isPendingDelete = Boolean(pendingDeleteAt);
    const isExpanded = expandedClientId === item.id;
    const contactsState = clientContacts[item.id] || { loading: false, items: [] };
    const itemStatus = normalizeClientStatus(item.status ?? item.client_status ?? item.clientStatus);
    return (
      <React.Fragment key={item.id}>
        <Tr
          onClick={() => handleView(item.id)}
          cursor="pointer"
          opacity={isPendingDelete ? 0.5 : 1}
        >
          <Td>
            <MiniIconButton
              aria-label={
                isExpanded
                  ? t("pilotClients.list.contactsToggle.hide")
                  : t("pilotClients.list.contactsToggle.show")
              }
              icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
              onClick={(e) => { e.stopPropagation(); handleToggleExpand(item.id); }}
            />
          </Td>
          <Td fontWeight="700" color="portal.text">{item.name}</Td>
          <Td>{item.sector || "-"}</Td>
          <Td>
            <StatusBadge
              label={getStatusLabel(t, CLIENT_STATUS_LABEL_KEYS, CLIENT_STATUS_LABELS, itemStatus)}
              colorScheme={CLIENT_STATUS_COLORS[itemStatus] || "gray"}
            />
          </Td>
          <Td>{item.location || "-"}</Td>
          <Td>{item.managerName || "-"}</Td>
          <Td>
            <HStack justify="flex-end" spacing="6px">
              {isPendingDelete && canConfirmDelete && (
                <MiniIconButton
                  aria-label={t("pilotClients.list.actions.confirmDelete")}
                  icon={<CheckIcon />}
                  color="portal.danger"
                  _hover={{ bg: "danger.50", borderColor: "portal.danger", color: "portal.danger" }}
                  onClick={(e) => { e.stopPropagation(); openDeleteModal(item); }}
                />
              )}
              <MiniIconButton
                aria-label="Edit"
                icon={<EditIcon />}
                onClick={(e) => { e.stopPropagation(); setEditingItem(item); editModal.onOpen(); }}
                isDisabled={isPendingDelete}
              />
              <MiniIconButton
                aria-label={t("pilotClients.list.actions.delete")}
                data-testid="delete-company-btn"
                icon={<DeleteIcon />}
                color="portal.danger"
                _hover={{ bg: "danger.50", borderColor: "portal.danger", color: "portal.danger" }}
                isDisabled={!canDelete || isPendingDelete}
                onClick={(e) => { e.stopPropagation(); openDeleteModal(item); }}
              />
            </HStack>
          </Td>
        </Tr>
        {isExpanded && (
          <Tr opacity={isPendingDelete ? 0.5 : 1}>
            {/* Keep first cell so the expanded content is indented under the client row */}
            <Td bg="portal.soft" sx={{ height: "auto !important" }} />
            <Td colSpan={6} bg="portal.soft" px="16px" py="12px" sx={{ height: "auto !important", whiteSpace: "normal !important" }}>
              <Box>
                {contactsState.loading ? (
                  <Flex align="center" justify="center" py="8px">
                    <Spinner size="sm" color="portal.primary" />
                  </Flex>
                ) : !contactsState.items || contactsState.items.length === 0 ? (
                  <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                    {t("pilotClients.list.contactsTable.noContacts")}
                  </Text>
                ) : (
                  <Box
                    overflowX="auto"
                    borderWidth="1px"
                    borderColor="portal.line"
                    borderRadius="10px"
                    bg="white"
                  >
                    <Table
                      size="sm"
                      sx={{
                        tableLayout: "fixed",
                        fontSize: "12px",
                        "& thead th": {
                          height: "30px",
                          py: 0,
                          px: "12px",
                          fontSize: "10px",
                          letterSpacing: "0.055em",
                          textTransform: "uppercase",
                          fontWeight: 900,
                          color: "portal.muted",
                          bg: "portal.soft",
                          borderColor: "portal.line",
                        },
                        "& tbody td": {
                          height: "38px",
                          py: 0,
                          px: "12px",
                          fontWeight: 600,
                          color: "#243543",
                          borderColor: "#edf2f5",
                        },
                        "& tbody tr:hover td": { bg: "#FBFEFE" },
                      }}
                    >
                      <Thead>
                        <Tr>
                          <Th>{t("pilotClients.list.contactsTable.name")}</Th>
                          <Th>{t("pilotClients.list.contactsTable.status")}</Th>
                          <Th>{t("pilotClients.list.contactsTable.email")}</Th>
                          <Th>{t("pilotClients.list.contactsTable.phone")}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {contactsState.items.map((c) => (
                          <Tr
                            key={c.id}
                            cursor="pointer"
                            onClick={() => handleContactRowClick(item.id)}
                          >
                            <Td fontWeight="700" color="portal.text">{c.name || "-"}</Td>
                            <Td>
                              {c.status ? (
                                <StatusBadge
                                  label={getStatusLabel(t, CONTACT_STATUS_LABEL_KEYS, CONTACT_STATUS_LABELS, c.status)}
                                  colorScheme={CONTACT_STATUS_COLORS[c.status] || "gray"}
                                />
                              ) : (
                                "-"
                              )}
                            </Td>
                            <Td>{c.email || "-"}</Td>
                            <Td>{c.phone || "-"}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </Box>
            </Td>
          </Tr>
        )}
      </React.Fragment>
    );
  };

  // Renders one contacts-view (flat) row.
  const renderContactRow = (item) => {
    const client = item.client || {};
    const clientId = client.id || item.client_id;
    const canOpenClient = Boolean(clientId);
    return (
      <Tr
        key={item.id}
        onClick={() => { if (canOpenClient) handleContactRowClick(clientId); }}
        cursor={canOpenClient ? "pointer" : "default"}
      >
        <Td fontWeight="700" color="portal.text">{item.name || "-"}</Td>
        <Td>{item.title || "-"}</Td>
        <Td>{item.division || "-"}</Td>
        <Td>
          {item.status ? (
            <StatusBadge
              label={getStatusLabel(t, CONTACT_STATUS_LABEL_KEYS, CONTACT_STATUS_LABELS, item.status)}
              colorScheme={CONTACT_STATUS_COLORS[item.status] || "gray"}
            />
          ) : (
            "-"
          )}
        </Td>
        <Td>{item.email || "-"}</Td>
        <Td>{item.phone || "-"}</Td>
        <Td>{client.name || "-"}</Td>
        <Td>{client.sector || "-"}</Td>
        <Td>{client.location || "-"}</Td>
        <Td>{client.managerName || "-"}</Td>
      </Tr>
    );
  };

  // Shared kanban-column shell for the two status board views.
  const renderKanbanColumn = ({ key, label, colorScheme, count, onDrop, children }) => (
    <Box
      key={key}
      flex="1 1 0"
      minW="240px"
      bg="portal.soft"
      borderWidth="1px"
      borderColor="portal.line"
      borderRadius="10px"
      p="12px"
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
    >
      <Flex align="center" justify="space-between" mb="12px">
        <StatusBadge label={label} colorScheme={colorScheme} />
        <Box
          as="span"
          fontSize="11px"
          fontWeight="800"
          color="portal.muted"
          bg="white"
          borderWidth="1px"
          borderColor="portal.line"
          px="7px"
          py="2px"
          borderRadius="999px"
        >
          {count}
        </Box>
      </Flex>
      <VStack align="stretch" spacing="10px" minH="100px">
        {children}
      </VStack>
    </Box>
  );

  return (
    // Bleed past PortalLayout's padding so the page-head spans the content edges.
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="CL"
        eyebrow={t("pilotClients.list.eyebrow", "ASAP PILOT")}
        title={pageTitle}
        actions={
          <>
            <Select
              value={viewMode}
              onChange={(e) => { setViewMode(e.target.value); setPage(1); }}
              w="160px"
              aria-label="Data view"
              sx={filterSelectSx}
            >
              <option value="companies">{t("pilotClients.list.view.companies", { defaultValue: "Companies" })}</option>
              <option value="clients">{t("pilotClients.list.view.contacts", { defaultValue: "Clients" })}</option>
              <option value="status">{t("pilotClients.list.view.companyStatus", { defaultValue: "Company Status" })}</option>
              <option value="contactStatus">{t("pilotClients.list.view.contactStatus", { defaultValue: "Contact Status" })}</option>
            </Select>
            {canExport && (
              <Button
                variant="secondary"
                size="sm"
                h="32px"
                leftIcon={<DownloadIcon boxSize="11px" />}
                onClick={handleExport}
                isLoading={exporting}
              >
                {t("pilotClients.list.actions.export", { defaultValue: "Export" })}
              </Button>
            )}
            {canConfirmDelete && pendingDeleteCount > 0 && (
              <Button
                variant="secondary"
                size="sm"
                h="32px"
                colorScheme="red"
                leftIcon={<DeleteIcon boxSize="11px" />}
                onClick={bulkDeleteModal.onOpen}
                data-testid="approve-all-deletions"
              >
                {t("pilotClients.list.actions.approveAllDeletions", { count: pendingDeleteCount })}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              h="32px"
              leftIcon={<Icon as={FiUpload} boxSize="13px" />}
              onClick={importWorkbookModal.onOpen}
              data-testid="open-import-workbook"
            >
              {t("pilotClients.list.actions.import")}
            </Button>
            {(isClientsView || isContactStatusView) && (
              <Button
                variant="secondary"
                size="sm"
                h="32px"
                leftIcon={<AddIcon boxSize="9px" />}
                onClick={createContactWithoutCompanyModal.onOpen}
                data-testid="create-contact-no-company"
              >
                {t("pilotClients.list.actions.createContactNoCompany", { defaultValue: "Add contact (no company)" })}
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              h="32px"
              leftIcon={<AddIcon boxSize="9px" />}
              onClick={createModal.onOpen}
            >
              {t("pilotClients.list.actions.create")}
            </Button>
          </>
        }
      />

      <Box p="20px">
        <Panel
          title={pageTitle}
          count={
            loading
              ? t("pilotClients.list.summary.loading")
              : t("pilotClients.list.summary.total", { count: totalItems })
          }
          filters={
            <Box w="100%">
              <FilterBar>
                <FilterSearch
                  placeholder={t("pilotClients.list.filters.searchPlaceholder")}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />
                <Input
                  placeholder={t("pilotClients.list.filters.sectorPlaceholder")}
                  value={filterSector}
                  onChange={(e) => { setFilterSector(e.target.value); setPage(1); }}
                  w="auto"
                  minW="180px"
                  sx={rangeInputSx}
                />
                <Select
                  placeholder={t("pilotClients.list.filters.statusPlaceholder")}
                  value={filterStatus}
                  onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                  w="auto"
                  minW="180px"
                  aria-label={t("pilotClients.list.filters.statusPlaceholder")}
                  sx={filterSelectSx}
                >
                  {statusFilterOptions.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Select>
                <Select
                  placeholder={t("pilotClients.list.filters.managerPlaceholder")}
                  value={filterManagerId}
                  onChange={(e) => { setFilterManagerId(e.target.value); setPage(1); }}
                  w="auto"
                  minW="200px"
                  sx={filterSelectSx}
                >
                  {buUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} - {u.email}
                    </option>
                  ))}
                </Select>
              </FilterBar>

              {/* Row 2: contact-created date range */}
              <Box mt="10px">
                <Text sx={{ ...filterLabelSx, marginBottom: "4px !important" }}>
                  {t("pilotClients.list.filters.contactCreatedLabel")}
                </Text>
                <HStack spacing="6px">
                  <Input
                    type="datetime-local"
                    placeholder={t("pilotClients.list.filters.fromPlaceholder")}
                    value={contactCreatedFrom}
                    onChange={(e) => { setContactCreatedFrom(e.target.value); setPage(1); }}
                    w="220px"
                    sx={rangeInputSx}
                  />
                  <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                    {t("pilotClients.list.filters.to", { defaultValue: "to" })}
                  </Text>
                  <Input
                    type="datetime-local"
                    placeholder={t("pilotClients.list.filters.toPlaceholder")}
                    value={contactCreatedTo}
                    onChange={(e) => { setContactCreatedTo(e.target.value); setPage(1); }}
                    w="220px"
                    sx={rangeInputSx}
                  />
                </HStack>
              </Box>
            </Box>
          }
          footer={
            isKanbanView ? null : (
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalItems={totalItems}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={(sz) => { setPageSize(sz); setPage(1); }}
              />
            )
          }
        >
          {isStatusView ? (
            <Flex gap="14px" p="16px" overflowX="auto" align="stretch">
              {CLIENT_STATUSES.map((status) => {
                const columnItems = items.filter((item) => (
                  normalizeClientStatus(item.status ?? item.client_status ?? item.clientStatus) === status.value
                ));
                return renderKanbanColumn({
                  key: status.value,
                  label: getStatusLabel(t, CLIENT_STATUS_LABEL_KEYS, CLIENT_STATUS_LABELS, status.value),
                  colorScheme: CLIENT_STATUS_COLORS[status.value] || "gray",
                  count: columnItems.length,
                  onDrop: (e) => {
                    const clientId = Number(e.dataTransfer.getData("text/plain"));
                    if (clientId) handleStatusDrop(clientId, status.value);
                  },
                  children:
                    columnItems.length === 0 ? (
                      <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                        {t("pilotClients.list.statusView.empty")}
                      </Text>
                    ) : (
                      columnItems.map((item) => {
                        const pendingDeleteAt = item.pendingDeleteAt || item.pending_delete_at;
                        const isPendingDelete = Boolean(pendingDeleteAt);
                        const topSkillsArr = normalizeTopSkills(item.topSkills ?? item.top_skills);
                        return (
                          <Box
                            key={item.id}
                            bg="white"
                            borderWidth="1px"
                            borderColor="portal.line"
                            borderRadius="10px"
                            p="10px"
                            boxShadow="panel"
                            cursor={isPendingDelete ? "not-allowed" : "grab"}
                            opacity={isPendingDelete ? 0.6 : 1}
                            draggable={!isPendingDelete}
                            onDragStart={(e) => {
                              e.dataTransfer.setData("text/plain", String(item.id));
                              e.dataTransfer.effectAllowed = "move";
                            }}
                            onClick={() => handleView(item.id)}
                            data-testid="client-kanban-card"
                            _hover={{ borderColor: "portal.primary" }}
                          >
                            <Text fontWeight="800" color="portal.text" fontSize="13px" sx={{ marginBottom: "2px !important" }}>
                              {item.name}
                            </Text>
                            <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                              {item.sector || "-"}
                            </Text>
                            <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                              {item.managerName || "-"}
                            </Text>
                            {topSkillsArr.length > 0 && (
                              <Wrap mt="8px" spacing="4px">
                                {topSkillsArr.slice(0, MAX_TOP_SKILLS_KANBAN).map((skill, idx) => (
                                  <WrapItem
                                    key={`${skill.name}-${idx}`}
                                    data-testid="client-kanban-top-skill"
                                    data-skill-family={skill.family || ""}
                                  >
                                    <Tag size="sm" colorScheme={familyColor(skill.family)} variant="subtle" borderRadius="999px">
                                      <TagLabel fontSize="10px">{skill.name}</TagLabel>
                                    </Tag>
                                  </WrapItem>
                                ))}
                              </Wrap>
                            )}
                          </Box>
                        );
                      })
                    ),
                });
              })}
            </Flex>
          ) : isContactStatusView ? (
            <Flex gap="14px" p="16px" overflowX="auto" align="stretch">
              {CONTACT_STATUSES.map((status) => {
                const columnItems = items.filter((item) => item.status === status.value);
                return renderKanbanColumn({
                  key: status.value,
                  label: getStatusLabel(t, CONTACT_STATUS_LABEL_KEYS, CONTACT_STATUS_LABELS, status.value),
                  colorScheme: CONTACT_STATUS_COLORS[status.value] || "gray",
                  count: columnItems.length,
                  onDrop: (e) => {
                    const contactId = Number(e.dataTransfer.getData("text/plain"));
                    if (contactId) handleContactStatusDrop(contactId, status.value);
                  },
                  children:
                    columnItems.length === 0 ? (
                      <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                        {t("pilotClients.list.contactStatusView.empty")}
                      </Text>
                    ) : (
                      columnItems.map((item) => {
                        const client = item.client || {};
                        const clientId = client.id || item.client_id;
                        const canOpenClient = Boolean(clientId);
                        const contactSkills = normalizeTopSkills(item.required_skills);
                        return (
                          <Box
                            key={item.id}
                            bg="white"
                            borderWidth="1px"
                            borderColor="portal.line"
                            borderRadius="10px"
                            p="10px"
                            boxShadow="panel"
                            cursor="grab"
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData("text/plain", String(item.id));
                              e.dataTransfer.effectAllowed = "move";
                            }}
                            onClick={() => { if (canOpenClient) handleContactRowClick(clientId); }}
                            _hover={{ borderColor: "portal.primary" }}
                          >
                            <Text fontWeight="800" color="portal.text" fontSize="13px" sx={{ marginBottom: "2px !important" }}>
                              {item.name || "-"}
                            </Text>
                            {client.name && (
                              <Text fontSize="12px" color="portal.muted" fontStyle="italic" sx={{ marginBottom: "0 !important" }}>
                                {client.name}
                              </Text>
                            )}
                            {item.email && (
                              <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                                {item.email}
                              </Text>
                            )}
                            {item.phone && (
                              <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                                {item.phone}
                              </Text>
                            )}
                            {contactSkills.length > 0 && (
                              <Wrap mt="8px" spacing="4px">
                                {contactSkills.slice(0, MAX_TOP_SKILLS_KANBAN).map((skill, idx) => (
                                  <WrapItem
                                    key={`${skill.name}-${idx}`}
                                    data-testid="contact-kanban-top-skill"
                                    data-skill-family={skill.family || ""}
                                  >
                                    <Tag size="sm" colorScheme={familyColor(skill.family)} variant="subtle" borderRadius="999px">
                                      <TagLabel fontSize="10px">{skill.name}</TagLabel>
                                    </Tag>
                                  </WrapItem>
                                ))}
                              </Wrap>
                            )}
                          </Box>
                        );
                      })
                    ),
                });
              })}
            </Flex>
          ) : (
            <DataTable
              columns={isClientsView ? contactColumns : companyColumns}
              rows={sortedItems}
              loading={loading}
              loadingRows={pageSize > 10 ? 10 : pageSize}
              renderRow={isClientsView ? renderContactRow : renderCompanyRow}
              emptyState={
                <EmptyState
                  icon={<FaBuilding />}
                  title={
                    isClientsView
                      ? t("pilotClients.list.table.noContacts")
                      : t("pilotClients.list.table.noItems")
                  }
                  hint={t("pilotClients.list.emptyHint", { defaultValue: "Create a company or contact to start building your client base." })}
                />
              }
            />
          )}
        </Panel>
      </Box>

      <ClientFormModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        initialData={null}
        onSuccess={onCreateSuccess}
      />
      <ClientFormModal
        isOpen={editModal.isOpen}
        onClose={() => { setEditingItem(null); editModal.onClose(); }}
        initialData={editingItem}
        onSuccess={onEditSuccess}
      />
      <ImportWorkbookModal
        isOpen={importWorkbookModal.isOpen}
        onClose={importWorkbookModal.onClose}
        onSuccess={() => { fetchData(); loadPendingDeleteCount(); }}
      />
      <ContactFormModal
        isOpen={createContactWithoutCompanyModal.isOpen}
        onClose={createContactWithoutCompanyModal.onClose}
        clientId={null}
        initialData={null}
        onSuccess={() => fetchData()}
      />
      <ClientContactsWizardModal
        isOpen={contactWizardModal.isOpen}
        onClose={() => {
          setCreatedClientForContacts(null);
          contactWizardModal.onClose();
        }}
        client={createdClientForContacts}
      />
      <ClientDetailModal
        isOpen={clientDetailModal.isOpen}
        onClose={() => {
          setSelectedClientId(null);
          clientDetailModal.onClose();
        }}
        clientId={selectedClientId}
        initialTabIndex={detailInitialTab}
      />
      {/* Permanent delete confirmation — impact summary + typed phrase */}
      <DeleteCompanyConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => { setDeleteTarget(null); deleteModal.onClose(); }}
        clientId={deleteTarget?.id}
        clientName={deleteTarget?.name}
        buId={selectedBusinessUnitId || undefined}
        onSuccess={() => { fetchData(); loadPendingDeleteCount(); }}
      />
      {/* Bulk approval of all Excel-initiated pending deletions */}
      <BulkApproveDeletionsModal
        isOpen={bulkDeleteModal.isOpen}
        onClose={bulkDeleteModal.onClose}
        buId={selectedBusinessUnitId || undefined}
        onSuccess={() => { fetchData(); loadPendingDeleteCount(); }}
      />
    </Box>
  );
};

export default Clients;
